/* Optional shipping subsidy information. Shopify remains the price authority. */
(() => {
  if (window.HomedockShippingBenefits) return;
  const pending = new WeakMap();
  let cartSession;
  const money = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
  const percentage = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 });

  const normalizeZip = value => String(value || '').replace(/\D/g, '').slice(0, 8);
  const formatZip = value => {
    const digits = normalizeZip(value);
    return digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
  };

  function bindZipInput(input) {
    if (!input || input.dataset.shippingZipBound) return;
    input.dataset.shippingZipBound = 'true';
    input.type = 'text';
    input.inputMode = 'numeric';
    // Native autofill can apply maxlength before firing input: reserve room for the hyphen.
    input.maxLength = 9;
    input.autocomplete = 'postal-code';
    input.pattern = '[0-9]{5}-?[0-9]{3}';
    input.value = formatZip(input.value);
    const applyMask = () => {
      const previous = input.value;
      const caret = input.selectionStart;
      const digitsBeforeCaret = normalizeZip(previous.slice(0, caret ?? previous.length)).length;
      const formatted = formatZip(previous);
      if (previous === formatted) return;
      input.value = formatted;
      if (document.activeElement === input && caret !== null) {
        const position = Math.min(formatted.length, digitsBeforeCaret + (digitsBeforeCaret > 5 ? 1 : 0));
        input.setSelectionRange(position, position);
      }
    };
    input.addEventListener('input', applyMask);
    input.addEventListener('change', applyMask);
    input.addEventListener('keydown', event => {
      const position = input.selectionStart;
      if (position !== input.selectionEnd || event.isComposing) return;
      if (event.key === 'Backspace' && input.value[position - 1] === '-') input.setSelectionRange(position - 1, position - 1);
      if (event.key === 'Delete' && input.value[position] === '-') input.setSelectionRange(position + 1, position + 1);
    });
    input.addEventListener('paste', event => {
      const pasted = event.clipboardData?.getData('text');
      if (pasted === undefined) return;
      event.preventDefault();
      const start = input.selectionStart ?? input.value.length;
      const end = input.selectionEnd ?? start;
      const prefix = normalizeZip(input.value.slice(0, start));
      const inserted = pasted.replace(/\D/g, '');
      input.value = formatZip(prefix + inserted + input.value.slice(end).replace(/\D/g, ''));
      const digitsBeforeCaret = Math.min(8, prefix.length + inserted.length);
      const position = digitsBeforeCaret + (digitsBeforeCaret > 5 ? 1 : 0);
      input.setSelectionRange(position, position);
      input.dispatchEvent(new Event('input', { bubbles: true }));
    });
  }

  function escapeHTML(value) {
    return String(value).replace(/[&<>"']/g, character => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;',
    }[character]));
  }

  function deliveryDate(rate) {
    let remaining = Array.isArray(rate.delivery_days) ? parseInt(rate.delivery_days[0], 10) : 0;
    if (!remaining || remaining < 1) return '';
    const date = new Date();
    while (remaining > 0) {
      date.setDate(date.getDate() + 1);
      if (![0, 6].includes(date.getDay())) remaining--;
    }
    return new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit' }).format(date);
  }

  function renderRates(rates) {
    return `<ul class="shipping-quote-list">${rates.map((rate, index) => {
      const amount = cents(rate.price);
      const free = amount === 0;
      const formatted = free ? 'Frete Grátis' : amount === null ? 'A consultar' : new Intl.NumberFormat('pt-BR', {
        style: 'currency', currency: /^[A-Z]{3}$/.test(rate.currency) ? rate.currency : 'BRL',
      }).format(amount / 100);
      const date = deliveryDate(rate);
      return `<li class="shipping-quote${free ? ' shipping-quote--free' : ''}" data-shipping-rate-index="${index}">
        <div class="shipping-quote-heading"><span class="shipping-quote-method">${escapeHTML(rate.name)}</span></div>
        <div class="shipping-calc-rate shipping-quote-price"><b${free ? ' class="shipping-calc-free"' : ''}>${escapeHTML(formatted)}</b></div>
        ${date ? `<div class="shipping-calc-delivery shipping-quote-delivery"><span>Entrega até</span><b>${date}</b></div>` : ''}
      </li>`;
    }).join('')}</ul>`;
  }

  function cents(value) {
    if (typeof value !== 'string' && typeof value !== 'number') return null;
    const match = /^(\d{1,9})(?:\.(\d{1,8}))?$/.exec(String(value));
    if (!match) return null;
    const fraction = (match[2] || '').padEnd(3, '0');
    const result = Number(match[1]) * 100 + Number(fraction.slice(0, 2)) + (Number(fraction[2]) >= 5 ? 1 : 0);
    return Number.isSafeInteger(result) && result <= 2000000000 ? result : null;
  }

  function readConfig(root) {
    try {
      const config = JSON.parse(root.querySelector('.js-shipping-benefit-data')?.textContent || 'null');
      if (!config || !['product', 'cart'].includes(config.mode) || !Array.isArray(config.products)) return null;
      if (config.preview_only !== false && !['unpublished', 'development'].includes(window.Shopify?.theme?.role)) return null;
      const url = new URL(config.endpoint);
      if (url.protocol !== 'https:' || url.username || url.password || url.search || url.hash) return null;
      if (!['', '/', '/quote'].includes(url.pathname)) return null;
      return { ...config, endpoint: `${url.origin}/quote` };
    } catch { return null; }
  }

  function product(input) {
    if (!input || input.requires_shipping !== true || !/^\d+$/.test(String(input.product_id)) || !/^\d+$/.test(String(input.variant_id))
      || typeof input.sku !== 'string' || !input.sku.trim() || input.sku.length > 120
      || !Number.isInteger(input.quantity) || input.quantity < 1 || input.quantity > 99
      || !Number.isInteger(input.price_cents) || input.price_cents < 0 || input.price_cents > 100000000) return null;
    const result = { ...input, product_id: String(input.product_id), variant_id: String(input.variant_id) };
    for (const name of ['weight_kg', 'width_cm', 'length_cm', 'height_cm']) {
      const raw = input[name];
      if (!['string', 'number'].includes(typeof raw) || !/^\d+(?:\.\d+)?$/.test(String(raw))) return null;
      result[name] = Number(raw);
      if (!Number.isFinite(result[name]) || result[name] <= 0 || result[name] > (name === 'weight_kg' ? 20000 : 3000)) return null;
    }
    return result;
  }

  function matchBenefit(rate, quote) {
    if (String(rate?.source || '').trim().toLowerCase() !== 'intelipost' || rate.currency !== 'BRL'
      || !/^\d{1,20}$/.test(String(rate.code || ''))) return null;
    const price = cents(rate.price);
    if (price === null || quote?.method_id !== String(rate.code) || quote.final_price_cents !== price) return null;
    const benefit = quote.benefit;
    if (!benefit || benefit.reference !== 'carrier_cost') return null;
    const { reference_cents: reference, amount_cents: amount, percentage_basis_points: basis } = benefit;
    if (![reference, amount, basis].every(Number.isSafeInteger) || reference > 2000000000
      || reference <= price || amount !== reference - price || amount <= 0
      || basis !== Math.floor(amount * 10000 / reference)) return null;
    return { reference, amount, basis, price };
  }

  function benefitText(benefit) {
    const amount = money.format(benefit.amount / 100);
    return benefit.price === 0
      ? `Você economiza ${amount} com frete grátis.`
      : `Você economiza ${amount} no frete.`;
  }

  function benefitLabel(benefit) {
    const rounded = Math.round(benefit.basis / 100);
    if (benefit.price === 0) return '100% de desconto';
    return rounded > 0 && rounded < 100 ? `${percentage.format(rounded)}% de desconto` : 'Frete com desconto';
  }

  function cartFingerprint(cart) {
    if (!cart || !Array.isArray(cart.items) || cart.total_discount !== 0
      || (cart.discount_codes || []).some(code => code.applicable)) return null;
    if (cart.items.some(item => !item.requires_shipping || item.selling_plan_allocation
      || Object.keys(item.properties || {}).length || (item.item_components || []).length
      || item.original_price !== item.final_price || (item.discount_allocations || []).length)) return null;
    return JSON.stringify(cart.items.map(item => [item.key, String(item.variant_id), item.quantity, item.original_price, item.grams]));
  }

  function cartMatches(cart, products) {
    if (!cartFingerprint(cart) || cart.items.length !== products.length) return false;
    return cart.items.every((item, index) => {
      const value = products[index];
      return String(item.variant_id) === value.variant_id && String(item.product_id) === value.product_id
        && item.quantity === value.quantity && item.original_price === value.price_cents
        && Math.abs(item.grams / 1000 - value.weight_kg) < 0.000001;
    });
  }

  function withCartSession(operation) {
    let task;
    try { task = cartSession ? cartSession.then(operation) : Promise.resolve(operation()); }
    catch (error) { task = Promise.reject(error); }
    const settled = task.catch(() => {});
    cartSession = settled;
    settled.then(() => { if (cartSession === settled) cartSession = null; });
    return task;
  }

  function getCart(signal) {
    return withCartSession(async () => {
      const base = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${base}cart.js`, { signal, cache: 'no-store', credentials: 'same-origin' });
      if (!response.ok) throw new Error('cart_unavailable');
      return response.json();
    });
  }

  function clear(root) {
    pending.get(root)?.abort();
    pending.delete(root);
    root.querySelectorAll('.homedock-shipping-benefit, .homedock-shipping-reference, .homedock-shipping-por, .homedock-shipping-badge').forEach(node => node.remove());
    root.querySelectorAll('.has-shipping-benefit').forEach(node => node.classList.remove('has-shipping-benefit'));
  }

  function showReference(row, benefit) {
    const container = row.querySelector('.shipping-calc-rate, .shipping-rate-option');
    const chargedPrice = container?.querySelector('b');
    if (!chargedPrice) return;
    const reference = document.createElement('span');
    reference.className = 'homedock-shipping-reference';
    const de = document.createElement('span');
    de.textContent = 'De ';
    de.setAttribute('aria-hidden', 'true');
    const description = document.createElement('span');
    description.className = 'visually-hidden';
    description.textContent = 'Valor de referência do transporte antes do subsídio: ';
    const original = document.createElement('s');
    original.textContent = money.format(benefit.reference / 100);
    reference.appendChild(de);
    reference.appendChild(description);
    reference.appendChild(original);
    const por = document.createElement('span');
    por.className = 'homedock-shipping-por';
    por.textContent = 'Por';
    container.insertBefore(reference, chargedPrice);
    container.insertBefore(por, chargedPrice);
    row.classList.add('has-shipping-benefit');
    const heading = row.querySelector('.shipping-quote-heading');
    if (heading) {
      const badge = document.createElement('span');
      badge.className = 'homedock-shipping-badge';
      badge.textContent = benefitLabel(benefit);
      heading.appendChild(badge);
    }
  }

  function applyBenefit(row, benefit, { showSavings = true } = {}) {
    showReference(row, benefit);
    if (!showSavings) return;
    const note = document.createElement('small');
    note.className = 'homedock-shipping-benefit';
    note.textContent = benefitText(benefit);
    row.appendChild(note);
  }

  function requestKey(config, products, destination) {
    return JSON.stringify([config.endpoint, destination, products]);
  }

  function prepareProduct({ root, zip, variantId, quantity }) {
    const config = readConfig(root);
    const destination = normalizeZip(zip);
    if (!config || config.mode !== 'product' || destination.length !== 8) return null;
    const products = config.products.filter(item => item?.variant_id === String(variantId))
      .map(item => product({ ...item, quantity }));
    if (!products.length || products.some(item => item === null)) return null;
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 9000);
    const promise = fetchQuote(config, products, destination, controller.signal)
      .catch(() => null).finally(() => window.clearTimeout(timeout));
    return { key: requestKey(config, products, destination), promise, abort: () => controller.abort() };
  }

  async function fetchQuote(config, products, destination, signal) {
    const response = await fetch(config.endpoint, {
      method: 'POST', signal, credentials: 'omit', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ destination_zip_code: destination, products: products.map(({ variant_id, requires_shipping, ...item }) => item) }),
    });
    if (!response.ok || signal.aborted) return null;
    const result = await response.json();
    return signal.aborted ? null : result;
  }

  async function enhance({ root, rates, zip, variantId, quantity, prepared, valid = () => true }) {
    clear(root);
    const config = readConfig(root);
    const destination = String(zip || '').replace(/\D/g, '');
    if (!config || !/^\d{8}$/.test(destination) || !rates?.some(rate => String(rate.source).trim().toLowerCase() === 'intelipost' && rate.currency === 'BRL')) return;
    const inputs = config.mode === 'product'
      ? config.products.filter(item => item?.variant_id === String(variantId)).map(item => ({ ...item, quantity }))
      : config.products;
    const products = inputs.map(product);
    if (!products.length || products.length > 50 || products.some(item => item === null)) return;
    const controller = new AbortController();
    pending.set(root, controller);
    const timeout = window.setTimeout(() => controller.abort(), 9000);
    const current = () => root.isConnected && pending.get(root) === controller && !controller.signal.aborted && valid();
    const abortPrepared = () => prepared?.abort();
    controller.signal.addEventListener('abort', abortPrepared, { once: true });
    try {
      let before;
      if (config.mode === 'cart') {
        before = await getCart(controller.signal);
        if (!cartMatches(before, products) || !current()) return;
      }
      if (prepared && prepared.key !== requestKey(config, products, destination)) return;
      const result = await (prepared ? prepared.promise : fetchQuote(config, products, destination, controller.signal));
      if (!result || !current() || result.version !== 1 || result.destination_zip_code !== destination || !Array.isArray(result.rates)) return;
      if (config.mode === 'cart' && cartFingerprint(await getCart(controller.signal)) !== cartFingerprint(before)) return;
      if (!current()) return;
      const ids = result.rates.map(rate => rate.method_id);
      if (new Set(ids).size !== ids.length) return;
      const benefits = rates.map(rate => matchBenefit(rate, result.rates.find(item => item.method_id === String(rate.code))));
      benefits.forEach((benefit, index) => {
        const row = root.querySelector(`[data-shipping-rate-index="${index}"]`);
        if (!benefit || !row) return;
        applyBenefit(row, benefit);
      });
      return benefits;
    } catch {
      // Optional information: failure retains Shopify's rate and delivery estimate.
    } finally {
      window.clearTimeout(timeout);
      controller.signal.removeEventListener('abort', abortPrepared);
      if (pending.get(root) === controller) pending.delete(root);
    }
  }

  window.HomedockShippingBenefits = {
    enhance, clear, prepareProduct, matchBenefit, benefitText, benefitLabel, cartFingerprint, cartMatches,
    normalizeZip, formatZip, bindZipInput, renderRates, deliveryDate, withCartSession, applyBenefit,
  };
})();
